import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { fetchTracks, clearCurrent } from '../../store/slices/tracksSlice';
import { ehTreinamento } from '../../utils/trilha';

export default function Sidebar({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useSelector((s) => s.auth);
  const tracks = useSelector((s) => s.tracks.list);

  // O vinculo vem do proprio /auth/me: se nao ha empresa, a secao inteira nao existe
  // (nao adianta oferecer /empresa para quem so receberia o aviso de 'sem_empresa').
  const empresa = user?.empresa || null;
  const ehGestor = empresa?.papel === 'gestor';

  // O gestor precisa descobrir a pendencia sem abrir o painel: enquanto a empresa esta
  // inadimplente o time inteiro volta a se comportar como Free, e o colaborador vai
  // esbarrar em cadeado sem entender o motivo.
  let avisoEmpresa = null;
  let detalheAviso = '';
  if (empresa?.situacao === 'inadimplente') {
    avisoEmpresa = 'pagamento pendente';
    detalheAviso = 'Pagamento pendente: o time voltou ao acesso gratuito até a regularização.';
  } else if (empresa?.situacao === 'cancelando') {
    // 'encerrado' aqui assustava sem motivo: o time continua estudando até o fim do
    // período que já foi pago.
    avisoEmpresa = 'cancelado';
    detalheAviso = 'Plano cancelado: o acesso vale até o fim do período já pago.';
  } else if (empresa?.situacao === 'cancelada') {
    avisoEmpresa = 'plano encerrado';
    detalheAviso = 'Plano encerrado: o time está no acesso gratuito.';
  }

  // A pagina de contato ganhou endereco novo, mas /consultorias segue valida por causa
  // dos links ja enviados. O NavLink so acende no proprio `to`, entao quem cai pela rota
  // antiga veria o menu sem nenhum item ativo — dai a checagem manual dos dois caminhos.
  const naPaginaDeContato =
    pathname.startsWith('/fale-com-a-ndm') || pathname.startsWith('/consultorias');

  // A badge depende da lista de trilhas, mas so Inicio, Cursos e Treinamentos a
  // carregavam: em /perfil ou /meus-certificados a lista chegava vazia e a badge sumia,
  // fazendo o menu se desmentir de pagina em pagina. Como a Sidebar esta montada em
  // todas as rotas autenticadas, e ela quem garante a lista. O `condition` do thunk
  // impede que isso vire um GET duplicado junto com o da pagina.
  useEffect(() => {
    if (user && tracks.length === 0) dispatch(fetchTracks());
  }, [user, tracks.length, dispatch]);

  // Lista vazia = ainda nao sabemos nada, entao nao chamamos atencao para Treinamentos.
  const semTreinamento = tracks.length > 0 && !tracks.some(ehTreinamento);

  const [confirmandoSaida, setConfirmandoSaida] = useState(false);

  const sair = () => {
    dispatch(logout());
    // Sair e entrar com outra conta e navegacao de SPA: sem zerar a lista aqui, o
    // proximo usuario ve as trilhas do anterior ate o fetch dele responder — e uma
    // trilha restrita apareceria para quem nao tem acesso.
    dispatch(clearCurrent());
    dispatch(fetchTracks.fulfilled([], 'logout'));
    navigate('/login');
  };

  const handleNav = () => {
    if (onClose) onClose();
  };

  // Esc fecha a confirmação. Quem abriu por engano tende a apertar Esc antes de procurar
  // o botão, e um diálogo que ignora a tecla parece travado.
  useEffect(() => {
    if (!confirmandoSaida) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape') setConfirmandoSaida(false); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [confirmandoSaida]);

  return (
    <aside className={`sidebar${open ? ' open' : ''}`}>
      <div className="sidebar-logo">
        <img
          src="/logo-ndm.png"
          alt="NDM"
          style={{ height: 30, filter: 'brightness(0) invert(1)', display: 'block' }}
        />
        <div className="logo-sub" style={{ marginTop: 6 }}>Academy</div>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section-label">Aprender</div>

        <NavLink to="/" end className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
          <i className="bi bi-house nav-icon"></i>
          Início
        </NavLink>

        <NavLink to="/cursos" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
          <i className="bi bi-collection-play nav-icon"></i>
          Cursos
        </NavLink>

        {/* Treinamento de cliente nao esta no catalogo: quem recebeu um codigo precisa
            de uma porta visivel, e o resgate mora dentro desta pagina. */}
        <NavLink to="/treinamentos" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
          <i className="bi bi-mortarboard nav-icon"></i>
          Treinamentos
          {semTreinamento && <span className="nav-badge">novo</span>}
        </NavLink>

        <div className="nav-section-label">Minha Conta</div>

        <NavLink to="/meus-certificados" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
          <i className="bi bi-award nav-icon"></i>
          Meus Certificados
        </NavLink>

        <NavLink to="/perfil" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
          <i className="bi bi-person-vcard nav-icon"></i>
          Meu Perfil
          {/* Sem CPF o certificado nao e emitido: o aviso fica visivel no menu
              ate a pessoa preencher. */}
          {!user?.cpf && <span className="nav-badge">CPF</span>}
        </NavLink>

        {empresa && (
          <>
            <div className="nav-section-label">Minha empresa</div>

            {/* A badge de aviso tem duas palavras e nao cabe ao lado do rotulo nos 240px
                da sidebar: sem o wrap ela espremia o texto do item. */}
            <NavLink
              to="/empresa"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              onClick={handleNav}
              title={detalheAviso || empresa.nome || undefined}
              style={avisoEmpresa ? { flexWrap: 'wrap' } : undefined}
            >
              <i className="bi bi-building nav-icon"></i>
              {ehGestor ? 'Painel da empresa' : 'Minha empresa'}
              {avisoEmpresa && <span className="nav-badge">{avisoEmpresa}</span>}
            </NavLink>
          </>
        )}

        <div className="nav-section-label">NDM</div>

        {/* Sem badge PRO: a pagina deixou de exigir assinatura, entao anunciar parede
            onde nao ha mais parede so afastava quem devia falar com a gente. */}
        <NavLink
          to="/fale-com-a-ndm"
          className={`nav-item${naPaginaDeContato ? ' active' : ''}`}
          onClick={handleNav}
        >
          <i className="bi bi-headset nav-icon"></i>
          Fale com a NDM
        </NavLink>

        <NavLink to="/assinatura" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
          <i className="bi bi-star nav-icon"></i>
          Assinatura
        </NavLink>

        <NavLink to="/newsletter" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
          <i className="bi bi-envelope nav-icon"></i>
          Newsletter
        </NavLink>

        {user?.role === 'admin' && (
          <>
            <div className="nav-section-label">Administração</div>

            <NavLink to="/admin" end className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
              <i className="bi bi-speedometer2 nav-icon"></i>
              Painel Admin
            </NavLink>

            <NavLink to="/admin/trilhas" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
              <i className="bi bi-collection-play nav-icon"></i>
              Trilhas e Vídeos
            </NavLink>

            <NavLink to="/admin/comentarios" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
              <i className="bi bi-chat-dots nav-icon"></i>
              Comentários
            </NavLink>

            <NavLink to="/admin/usuarios" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
              <i className="bi bi-people nav-icon"></i>
              Usuários
            </NavLink>

            <NavLink to="/admin/empresas" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} onClick={handleNav}>
              <i className="bi bi-buildings nav-icon"></i>
              Empresas
            </NavLink>
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        {/* Abre a confirmação em vez de sair direto: o card ocupa a largura toda do rodapé
            e um clique de raspão custava a sessão inteira — inclusive o progresso de quem
            estava no meio de uma aula. */}
        <div className="user-card" onClick={() => setConfirmandoSaida(true)} title="Sair da conta">
          <div className="user-avatar">
            {user?.avatarInitials || user?.name?.substring(0, 2).toUpperCase() || 'ND'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </div>
            {/* user.company e texto livre digitado no cadastro; empresa.nome e o vinculo
                que a plataforma reconhece. Quando os dois divergem, manda o vinculo. */}
            <div className="user-role">
              {user?.role === 'admin' ? 'Administrador' : empresa?.nome || user?.company || 'Aluno'}
            </div>
          </div>
          <i className="bi bi-box-arrow-right" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}></i>
        </div>
      </div>

      {confirmandoSaida && (
        <div
          className="confirmacao-fundo"
          onClick={() => setConfirmandoSaida(false)}
          role="presentation"
        >
          <div
            className="confirmacao-caixa"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-sair"
          >
            <div id="titulo-sair" className="confirmacao-titulo">Sair da conta?</div>
            <p className="confirmacao-texto">
              Você precisará entrar de novo com e-mail e senha. Seu progresso e seus
              certificados ficam guardados.
            </p>
            <div className="confirmacao-acoes">
              <button
                type="button"
                className="btn-ndm btn-ndm-outline btn-sm-ndm"
                onClick={() => setConfirmandoSaida(false)}
                autoFocus
              >
                Continuar conectado
              </button>
              <button type="button" className="btn-ndm btn-ndm-dark btn-sm-ndm" onClick={sair}>
                <i className="bi bi-box-arrow-right me-2"></i>Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
